/**
 * @fileoverview Case Ingestion Assist Types - Types for the CMS import assist system.
 *
 * Defines the import artifact contract, imported field presence semantics,
 * and step-shaped payload types aligned with UNIFIED_STEPS.
 *
 * Presence semantics:
 * - `present`: Value was found in the source CMS and imported.
 * - `missing`: Value was expected but not found in the source CMS.
 * - `not_applicable`: Field does not apply to this case type in the source CMS.
 */
import { z } from 'zod';

// =============================================================================
// IMPORTED FIELD PRESENCE
// =============================================================================

export const IMPORTED_FIELD_PRESENCE = ['present', 'missing', 'not_applicable'] as const;
export type ImportedFieldPresence = (typeof IMPORTED_FIELD_PRESENCE)[number];

// =============================================================================
// IMPORTED FIELD WRAPPER
// =============================================================================

/**
 * Wraps an imported value with presence metadata.
 * Every field in the import payload uses this shape.
 */
export interface ImportedField<T = string> {
  value: T | null;
  presence: ImportedFieldPresence;
}

// =============================================================================
// STEP-SHAPED IMPORT PAYLOADS
// =============================================================================

/** Case Info step imported fields */
export interface ImportedCaseInfo {
  firm_name: ImportedField;
  representing_attorney: ImportedField;
  date_of_loss: ImportedField;
  statute_of_limitations_date: ImportedField;
  incident_location: ImportedField;
  incident_summary: ImportedField;
  case_type: ImportedField;
  case_id: ImportedField;
  external_cms_url: ImportedField;
}

/** Client Info step imported fields */
export interface ImportedClientInfo {
  first_name: ImportedField;
  last_name: ImportedField;
  email: ImportedField;
  phone_number: ImportedField;
  date_of_birth: ImportedField;
  ssn_last_four: ImportedField;
  address: ImportedField<ImportedAddress>;
}

/** Structured address imported from CMS */
export interface ImportedAddress {
  line1: string;
  line2: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
}

/** Liability step imported fields */
export interface ImportedLiability {
  police_report_number: ImportedField;
  police_report_date: ImportedField;
  police_department: ImportedField;
  at_fault_party: ImportedField;
  liability_notes: ImportedField;
}

/** Treatment step imported fields */
export interface ImportedTreatment {
  providers: ImportedField<ImportedProvider[]>;
  treatment_notes: ImportedField;
}

/** A single imported medical provider */
export interface ImportedProvider {
  name: string;
  type: string | null;
  phone: string | null;
  address: string | null;
}

/** Coverage step imported fields */
export interface ImportedCoverage {
  first_party: ImportedField<ImportedCoverageEntry[]>;
  third_party: ImportedField<ImportedCoverageEntry[]>;
  health_insurance: ImportedField<ImportedCoverageEntry[]>;
  coverage_notes: ImportedField;
}

/** A single imported coverage entry */
export interface ImportedCoverageEntry {
  carrier_name: string;
  policy_number: string | null;
  claim_number: string | null;
  adjuster_name: string | null;
  adjuster_phone: string | null;
}

/** Top-level import payload aligned with UNIFIED_STEPS */
export interface ImportPayload {
  case_info: ImportedCaseInfo;
  client_info: ImportedClientInfo;
  liability: ImportedLiability;
  treatment: ImportedTreatment;
  coverage: ImportedCoverage;
  /** Free-text notes from the CMS that don't map to structured fields */
  source_notes: string | null;
}

// =============================================================================
// IMPORT ARTIFACT (API response shape)
// =============================================================================

/**
 * The persisted import artifact stored on the case-ingestion row.
 * One per matter in v1.
 *
 * Note: No `id` field — the import lives on the CaseIngestionResult row,
 * not a separate table. The backend returns `matter_id` as the identifier.
 */
export interface CaseIngestionImport {
  matter_id: string;
  imported_at: string;
  payload: ImportPayload;
}

// =============================================================================
// REQUEST / RESPONSE TYPES
// =============================================================================

/** Request body for creating an import — just the payload */
export interface CreateCaseIngestionImportRequest {
  payload: ImportPayload;
}

// =============================================================================
// ZOD SCHEMAS (for frontend validation of pasted JSON)
// =============================================================================

const ImportedFieldSchema = z.object({
  value: z.string().nullable(),
  presence: z.enum(IMPORTED_FIELD_PRESENCE),
});

const ImportedAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postcode: z.string().nullable(),
});

const ImportedFieldWithAddressSchema = z.object({
  value: ImportedAddressSchema.nullable(),
  presence: z.enum(IMPORTED_FIELD_PRESENCE),
});

const ImportedProviderSchema = z.object({
  name: z.string(),
  type: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
});

const ImportedCoverageEntrySchema = z.object({
  carrier_name: z.string(),
  policy_number: z.string().nullable(),
  claim_number: z.string().nullable(),
  adjuster_name: z.string().nullable(),
  adjuster_phone: z.string().nullable(),
});

const ImportedFieldWithProvidersSchema = z.object({
  value: z.array(ImportedProviderSchema).nullable(),
  presence: z.enum(IMPORTED_FIELD_PRESENCE),
});

const ImportedFieldWithCoverageSchema = z.object({
  value: z.array(ImportedCoverageEntrySchema).nullable(),
  presence: z.enum(IMPORTED_FIELD_PRESENCE),
});

export const ImportPayloadSchema = z.object({
  case_info: z.object({
    firm_name: ImportedFieldSchema,
    representing_attorney: ImportedFieldSchema,
    date_of_loss: ImportedFieldSchema,
    statute_of_limitations_date: ImportedFieldSchema,
    incident_location: ImportedFieldSchema,
    incident_summary: ImportedFieldSchema,
    case_type: ImportedFieldSchema,
    case_id: ImportedFieldSchema,
    external_cms_url: ImportedFieldSchema,
  }),
  client_info: z.object({
    first_name: ImportedFieldSchema,
    last_name: ImportedFieldSchema,
    email: ImportedFieldSchema,
    phone_number: ImportedFieldSchema,
    date_of_birth: ImportedFieldSchema,
    ssn_last_four: ImportedFieldSchema,
    address: ImportedFieldWithAddressSchema,
  }),
  liability: z.object({
    police_report_number: ImportedFieldSchema,
    police_report_date: ImportedFieldSchema,
    police_department: ImportedFieldSchema,
    at_fault_party: ImportedFieldSchema,
    liability_notes: ImportedFieldSchema,
  }),
  treatment: z.object({
    providers: ImportedFieldWithProvidersSchema,
    treatment_notes: ImportedFieldSchema,
  }),
  coverage: z.object({
    first_party: ImportedFieldWithCoverageSchema,
    third_party: ImportedFieldWithCoverageSchema,
    health_insurance: ImportedFieldWithCoverageSchema,
    coverage_notes: ImportedFieldSchema,
  }),
  source_notes: z.string().nullable(),
});

export const CreateImportRequestSchema = z.object({
  payload: ImportPayloadSchema,
});
